/**
 * Phase C (perceived-latency): decide whether to schedule a local hit-time preview of the player's
 * OWN attack outcome, so damage/deaths render when the swing lands instead of after the
 * attack-complete round-trip.
 *
 * This is a pure decision function (no side effects) so it can be unit-tested in isolation. The
 * actual apply is done by BattleUI's applyAttackHitOutcomePreview — the exact same path spectators
 * already use — and the authoritative attack-complete echo reconciles afterward.
 */

/** Delay from attack-initiated to the local hit-time preview. Mirrors the spectator preview timing. */
export const ATTACK_HIT_PREVIEW_DELAY_MS = 420;

/**
 * @param {Object} params
 * @param {boolean} params.enabled            OPTIMISTIC_ATTACK_HIT_PREVIEW_ENABLED kill switch.
 * @param {boolean} params.isSpectator        Spectators have their own preview path; never double up.
 * @param {string}  params.attackId
 * @param {Object}  params.outcome            The attack outcome from attack-initiated.
 * @param {boolean} params.alreadyScheduled   A preview timeout is already pending for this attackId.
 * @param {boolean} params.alreadyApplied      The preview was already applied for this attackId.
 * @param {boolean} params.alreadyCompleted    attack-complete already resolved this attackId.
 * @returns {boolean}
 */
export function shouldScheduleAttackHitPreview({
  enabled,
  isSpectator,
  attackId,
  outcome,
  alreadyScheduled,
  alreadyApplied,
  alreadyCompleted,
}) {
  if (enabled !== true) {
    return false;
  }
  if (isSpectator === true) {
    return false;
  }
  if (!attackId || !outcome) {
    return false;
  }
  if (alreadyScheduled === true || alreadyApplied === true || alreadyCompleted === true) {
    return false;
  }
  // Only schedule when the outcome carries something previewable locally. If none of these are
  // present we cannot render an accurate hit-time state, so we let attack-complete drive the update.
  const hasPreviewableHealth =
    typeof outcome.targetHealthAfter === 'number' ||
    typeof outcome.attackerHealthAfter === 'number' ||
    outcome.isGeneralAttack === true ||
    outcome.targetZone === 'general';

  return hasPreviewableHealth;
}
